// components/auth/PasskeyManager.tsx
import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";

interface PasskeyItem {
  passkey_id: string;
  device_name: string;
  device_type: string;
  created_at: string;
  last_used_at: string | null;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const str = atob(base64);
  const bytes = new Uint8Array(str.length);

  for (let i = 0; i < str.length; i++) {
    bytes[i] = str.charCodeAt(i);
  }

  return bytes.buffer;
}

export default function PasskeyManager() {
  const {
    passkeyRegisterOptions,
    passkeyRegisterVerify,
    passkeyLoginOptions,
    passkeyLoginVerify,
    getPasskeys,
    deletePasskey,
    user,
  } = useAuth();

  const [loading, setLoading] = useState(false);
  const [passkeys, setPasskeys] = useState<PasskeyItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  /* -------------------------------- */
  /* Load passkeys                   */
  /* -------------------------------- */

  const loadPasskeys = async () => {
    try {
      const data = await getPasskeys();
      setPasskeys(data);
    } catch (err: any) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (user) {
      loadPasskeys();
    }
  }, [user]);

  /* -------------------------------- */
  /* Register Passkey                */
  /* -------------------------------- */

  const handleCreatePasskey = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await passkeyRegisterOptions();

      const options = res.options;

      options.challenge = base64urlToBuffer(options.challenge);

      options.user.id = base64urlToBuffer(options.user.id);

      if (options.excludeCredentials?.length) {
        options.excludeCredentials = options.excludeCredentials.map(
          (cred: any) => ({
            ...cred,
            id: base64urlToBuffer(cred.id),
          })
        );
      }

      const credential = (await navigator.credentials.create({
        publicKey: options,
      })) as PublicKeyCredential;

      if (!credential) {
        throw new Error("Failed to create passkey");
      }

      const response = credential.response as AuthenticatorAttestationResponse;

      const credentialPayload = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: bufferToBase64url(
            response.attestationObject
          ),
          clientDataJSON: bufferToBase64url(
            response.clientDataJSON
          ),
        },
      };

      await passkeyRegisterVerify(credentialPayload);

      await loadPasskeys();

      alert("Passkey created successfully");
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Passkey creation failed");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------- */
  /* Login With Passkey              */
  /* -------------------------------- */

  const handlePasskeyLogin = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await passkeyLoginOptions();

      const options = res.options;

      options.challenge = base64urlToBuffer(options.challenge);

      if (options.allowCredentials?.length) {
        options.allowCredentials = options.allowCredentials.map(
          (cred: any) => ({
            ...cred,
            id: base64urlToBuffer(cred.id),
          })
        );
      }

      const credential = (await navigator.credentials.get({
        publicKey: options,
      })) as PublicKeyCredential;

      if (!credential) {
        throw new Error("Passkey login failed");
      }

      const response = credential.response as AuthenticatorAssertionResponse;

      const credentialPayload = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: bufferToBase64url(
            response.authenticatorData
          ),
          clientDataJSON: bufferToBase64url(
            response.clientDataJSON
          ),
          signature: bufferToBase64url(response.signature),
          userHandle: response.userHandle
            ? bufferToBase64url(response.userHandle)
            : null,
        },
      };

      await passkeyLoginVerify(credentialPayload);

      alert("Logged in with passkey");
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Passkey login failed");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------- */
  /* Delete Passkey                  */
  /* -------------------------------- */

  const handleDelete = async (passkeyId: string) => {
    const ok = confirm("Delete this passkey?");
    if (!ok) return;

    try {
      await deletePasskey(passkeyId);
      await loadPasskeys();
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Delete failed");
    }
  };

  /* -------------------------------- */
  /* UI                              */
  /* -------------------------------- */

  return (
    <div className="w-full max-w-2xl mx-auto space-y-6">
      <div className="rounded-2xl border p-6 bg-white dark:bg-zinc-900">
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="text-2xl font-bold">
              Passkeys
            </h2>

            <p className="text-sm text-zinc-500 mt-1">
              Secure passwordless authentication using your device biometrics.
            </p>
          </div>

          {error && (
            <div className="rounded-xl bg-red-100 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleCreatePasskey}
              disabled={loading}
              className="px-5 py-3 rounded-xl bg-black text-white disabled:opacity-50"
            >
              {loading
                ? "Processing..."
                : "Create Passkey"}
            </button>

            <button
              onClick={handlePasskeyLogin}
              disabled={loading}
              className="px-5 py-3 rounded-xl border"
            >
              Login With Passkey
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border p-6 bg-white dark:bg-zinc-900">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold">
            Your Passkeys
          </h3>

          <button
            onClick={loadPasskeys}
            className="text-sm underline"
          >
            Refresh
          </button>
        </div>

        {passkeys.length === 0 ? (
          <div className="text-sm text-zinc-500">
            No passkeys registered.
          </div>
        ) : (
          <div className="space-y-3">
            {passkeys.map((item) => (
              <div
                key={item.passkey_id}
                className="border rounded-xl p-4 flex items-center justify-between"
              >
                <div>
                  <div className="font-medium">
                    {item.device_name || "Unknown Device"}
                  </div>

                  <div className="text-sm text-zinc-500">
                    {item.device_type}
                  </div>

                  <div className="text-xs text-zinc-400 mt-1">
                    Created:{" "}
                    {new Date(
                      item.created_at
                    ).toLocaleString()}
                  </div>

                  {item.last_used_at && (
                    <div className="text-xs text-zinc-400">
                      Last used:{" "}
                      {new Date(
                        item.last_used_at
                      ).toLocaleString()}
                    </div>
                  )}
                </div>

                <button
                  onClick={() =>
                    handleDelete(item.passkey_id)
                  }
                  className="px-4 py-2 rounded-lg border text-red-500"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}